import React, { useEffect, useMemo, useState, createContext } from 'react';
import { useLocation, matchPath } from 'react-router-dom';
import { SYSTEMS_CONFIG } from './constants';
import { sidebarHooks } from '.';
import { SideBar } from '.'
import useFlattenedRoutes from 'router/hooks/useFlattenedRoutes';

export const SideBarContext = createContext();

/**
 * SideBarProvider - מנהל את המצב הלוגי של סרגל הצד.
 * אחראי על סנכרון בין ה-URL הנוכחי לבין המערכת והפריט הנבחר.
 */
const SideBarProvider = () => {
    const location = useLocation();
    const allRoutes = useFlattenedRoutes(); // מקבלים את כל הניתובים, כולל המקוננים
    const menuItems = sidebarHooks.useMenuItems();

    const [system, setSystem] = useState(SYSTEMS_CONFIG.school);
    const [selectedItem, setSelectedItem] = useState(null);
    const [selectedSubItem, setSelectedSubItem] = useState(null);

    // יצירת מפת חיפוש מהירה (O(1)) לכל הפריטים בתפריט
    const routeLookup = useMemo(() => {
        const cache = new Map();
        menuItems.forEach(item => {
            const base = { parentName: item.name, systemId: item.system };
            if (item.subItems?.length) {
                item.subItems.forEach(sub => cache.set(sub.name, { ...base, subItemName: sub.name }));
            }
            cache.set(item.name, { ...base, subItemName: null });
        });
        return cache;
    }, [menuItems]);

    // סנכרון אוטומטי של הממשק עם שינויי ניתוב
    useEffect(() => {
        // 1. חיפוש הראוט מתוך הרשימה השטוחה (יעבוד לנתיבים רגילים)
        const currentRoute = allRoutes.find(r => 
            matchPath({ path: r.path, end: true }, location.pathname)
        );

        let sidebarItemName = currentRoute?.sideBarItem;

        // 2. ✨ גיבוי (Fallback) חכם לסיידבאר ✨
        // אם הפונקציה לא מצאה את הראוט (כי זה נתיב צאצא יחסי מקונן),
        // נחפש את הכתובת ישירות בתוך פריטי התפריט שהגדרנו עם נתיבים מוחלטים!
        if (!sidebarItemName) {
            menuItems.forEach(item => {
                // קודם בודקים אם יש התאמה לפריט האב הראשי
                if (item.path && matchPath({ path: item.path, end: false }, location.pathname)) {
                    sidebarItemName = item.name;
                }
                // בודקים תתי-פריטים - אם יש התאמה מדויקת זה ידרוס את פריט האב
                item.subItems?.forEach(sub => {
                    if (sub.path && matchPath({ path: sub.path, end: true }, location.pathname)) {
                        sidebarItemName = sub.name;
                    }
                });
            });
        }

        // 3. ממשיכים את הלוגיקה שלך בדיוק כפי שהייתה
        if (sidebarItemName && routeLookup.has(sidebarItemName)) {
            const match = routeLookup.get(sidebarItemName);

            setSelectedItem(match.parentName);
            setSelectedSubItem(match.subItemName);

            // עדכון המערכת רק אם היא באמת השתנתה (ביצועים)
            if (system.id !== match.systemId) {
                setSystem(SYSTEMS_CONFIG[match.systemId] || SYSTEMS_CONFIG.school);
            }
        }
    }, [location.pathname, routeLookup, allRoutes, system.id, menuItems]);

    const value = useMemo(() => ({
        system, setSystem,
        selectedItem, setSelectedItem,
        selectedSubItem, setSelectedSubItem,
    }), [system, selectedItem, selectedSubItem]);

    return (
        <SideBarContext.Provider value={value}>
            <SideBar />
        </SideBarContext.Provider>
    );
};

export default SideBarProvider;